import axios from 'axios'

import type { Fields, Reports, Template, WarningLevel, AssignedReports } from './libraryModels'

const API_URL = process.env.REACT_APP_API_URL

export type LibraryTemplateFilters = {
  companyGroupAdminId?: number
  companyAdminId?: number
  formType?: string
}

export type LibraryApiFeatures = {
  showModeToggle: boolean
  showCompanyFilters: boolean
  showVesselFilters: boolean
  showAssignReports: boolean
  useAssignments: boolean
  allowConsumptionTab: boolean
  allowEditInCompanyMode: boolean
  showFormTypeSelect: boolean
}

export type LibraryApi = {
  id: 'qhse'
  defaultMode: 'master' | 'company'
  features: LibraryApiFeatures
  getTemplateList: (filters?: LibraryTemplateFilters) => Promise<Template[]>
  getReportsForTemplate: (templateId: number) => Promise<Reports[]>
  getAssignedTemplatesByCompanyAdmin?: (companyAdminId: number) => Promise<AssignedReports[]>
  getAssignedTemplatesByCompany?: (companyAdminId: number, companyId: number) => Promise<AssignedReports[]>
  getAssignedTemplatesByVesselType?: (
    companyAdminId: number,
    companyId: number,
    vesselType: string
  ) => Promise<AssignedReports[]>
  getAssignedTemplatesByVesselId?: (
    companyAdminId: number,
    companyId: number,
    vesselType: string,
    vesselId: number
  ) => Promise<AssignedReports[]>
  assignReport?: (companyAdminId: number, templateId: number) => Promise<unknown>
  createTemplate: (name: string, filters?: LibraryTemplateFilters) => Promise<{ id: number }>
  deleteTemplate: (templateId: number) => Promise<unknown>
  createMenuItem: (templateId: number, reportName: string, active: boolean) => Promise<unknown>
  updateMenuItem: (
    templateId: number,
    reportId: number,
    reportName: string,
    active: boolean,
    order?: number
  ) => Promise<unknown>
  createSubmenuItem: (
    menuId: number,
    name: string,
    active: boolean,
    order?: number
  ) => Promise<{ id: number }>
  updateSubMenuItem: (
    menuId: number,
    submenuId: number,
    name: string,
    active: boolean,
    order?: number
  ) => Promise<unknown>
  deleteSubMenuItem: (menuId: number, submenuId: number) => Promise<unknown>
  createField: (
    submenuId: number,
    label: string,
    fieldType: string,
    readOnly: boolean,
    required: boolean,
    active?: boolean,
    warningOnly?: WarningLevel,
    unit?: string,
    minValue?: number,
    maxValue?: number,
    options?: string[] | null
  ) => Promise<unknown>
  updateField: (
    submenuId: number,
    fieldId: number,
    label: string,
    fieldType: string,
    required?: boolean,
    readOnly?: boolean,
    warningOnly?: WarningLevel,
    active?: boolean,
    unit?: string,
    minValue?: number,
    maxValue?: number,
    options?: string[],
    order?: number
  ) => Promise<unknown>
  // Used by some layouts when fields belong to a "custom template" rather than a submenu.
  // Optional because not all backends support this endpoint.
  updateCustomField?: (
    templateId: number,
    fieldId: number,
    label: string,
    fieldType: string,
    readOnly: boolean,
    required: boolean,
    warningOnly?: WarningLevel,
    active?: boolean,
    unit?: string,
    minValue?: number,
    maxValue?: number,
    options?: string[],
    order?: number
  ) => Promise<unknown>
  deleteField: (submenuId: number, fieldId: number) => Promise<unknown>
  getFieldsForTab?: (tabId: number) => Promise<Fields[]>
}

type QhseMenuResponse = {
  id: number
  name: string
  order?: number
  isActive: boolean
  templateId?: number
  submenus?: QhseSubmenuResponse[]
}

type QhseSubmenuResponse = {
  id: number
  name: string
  isActive: boolean
  menuId?: number
  fields?: QhseFieldResponse[]
}

type QhseFieldResponse = {
  id: number
  label: string
  fieldType: string
  readOnly: boolean
  isActive: boolean
  order?: number
  optionsJson?: string[]
  required: boolean
  unit?: string
  minValue?: number
  maxValue?: number
  warningOnly?: WarningLevel
  submenuId?: number
}

const mapQhseField = (field: QhseFieldResponse): Fields => ({
  id: field.id,
  label: field.label,
  fieldType: field.fieldType,
  readOnly: field.readOnly ?? false,
  isActive: field.isActive,
  order: field.order,
  optionsJson: field.optionsJson,
  required: field.required,
  unit: field.unit,
  minValue: field.minValue,
  maxValue: field.maxValue,
  warningOnly: field.warningOnly,
  submenuId: field.submenuId ?? 0,
})

const mapQhseSubmenu = (submenu: QhseSubmenuResponse): Reports['submenus'][number] => ({
  id: submenu.id,
  name: submenu.name,
  isActive: submenu.isActive,
  fields: (submenu.fields ?? []).map(mapQhseField),
  menuId: submenu.menuId ?? 0,
})

const mapQhseMenuToReport = (menu: QhseMenuResponse): Reports => ({
  id: menu.id,
  name: menu.name,
  order: menu.order ?? 0,
  templateId: menu.templateId ?? 0,
  isActive: menu.isActive,
  submenus: (menu.submenus ?? []).map(mapQhseSubmenu),
})

const qhseCreateTemplatePayload = (name: string, filters?: LibraryTemplateFilters) => ({
  name,
  description: '',
  formType: filters?.formType ?? 'FORM',
  isActive: true,
  companyGroupAdminId: filters?.companyGroupAdminId,
  companyAdminId: filters?.companyAdminId,
})

export const qhseLibraryApi: LibraryApi = {
  id: 'qhse',
  defaultMode: 'company',
  features: {
    showModeToggle: false,
    showCompanyFilters: true,
    showVesselFilters: false,
    showAssignReports: false,
    useAssignments: false,
    allowConsumptionTab: false,
    allowEditInCompanyMode: true,
    showFormTypeSelect: true,
  },
  getTemplateList: async (filters?: LibraryTemplateFilters) => {
    const params = {
      companyGroupId: filters?.companyGroupAdminId,
      companyAdminId: filters?.companyAdminId,
      formType: filters?.formType,
    }
    const response = await axios.get(`${API_URL}/qhse/forms/templates`, { params })
    return response.data
  },
  getReportsForTemplate: async (templateId: number) => {
    const response = await axios.get(`${API_URL}/qhse/forms/templates/${templateId}/menus`)
    const menus: QhseMenuResponse[] = response.data ?? []
    return menus.map(mapQhseMenuToReport)
  },
  createTemplate: async (name: string, filters?: LibraryTemplateFilters) => {
    if (!filters?.companyGroupAdminId) {
      throw new Error('companyGroupAdminId is required to create QHSE templates')
    }
    const response = await axios.post(
      `${API_URL}/qhse/forms/templates/create`,
      qhseCreateTemplatePayload(name, filters)
    )
    return response.data
  },
  deleteTemplate: async (templateId: number) => {
    const response = await axios.delete(`${API_URL}/qhse/forms/templates/delete/${templateId}`)
    return response.data
  },
  createMenuItem: async (templateId: number, reportName: string, active: boolean) => {
    const response = await axios.post(`${API_URL}/qhse/forms/templates/${templateId}/menus/create`, {
      name: reportName,
      isActive: active,
    })
    return response.data
  },
  updateMenuItem: async (templateId: number, reportId: number, reportName: string, active: boolean, order?: number) => {
    const response = await axios.put(`${API_URL}/qhse/forms/templates/${templateId}/menus/update/${reportId}`, {
      name: reportName,
      isActive: active,
      order,
    })
    return response.data
  },
  createSubmenuItem: async (menuId: number, name: string, active: boolean, order?: number) => {
    const response = await axios.post(`${API_URL}/qhse/forms/menus/${menuId}/submenus/create`, {
      name,
      isActive: active,
      order,
    })
    return response.data
  },
  updateSubMenuItem: async (menuId: number, submenuId: number, name: string, active: boolean, order?: number) => {
    const response = await axios.put(`${API_URL}/qhse/forms/menus/${menuId}/submenus/update/${submenuId}`, {
      name,
      isActive: active,
      order,
    })
    return response.data
  },
  deleteSubMenuItem: async (menuId: number, submenuId: number) => {
    const response = await axios.delete(`${API_URL}/qhse/forms/menus/${menuId}/submenus/delete/${submenuId}`)
    return response.data
  },
  createField: async (
    submenuId: number,
    label: string,
    fieldType: string,
    readOnly: boolean,
    required: boolean,
    active?: boolean,
    warningOnly?: WarningLevel,
    unit?: string,
    minValue?: number,
    maxValue?: number,
    options?: string[] | null
  ) => {
    const response = await axios.post(`${API_URL}/qhse/forms/submenus/${submenuId}/fields/create`, {
      label,
      fieldType,
      readOnly,
      required,
      isActive: active,
      warningOnly,
      unit,
      minValue,
      maxValue,
      optionsJson: options ?? undefined,
    })
    return response.data
  },
  updateField: async (
    submenuId: number,
    fieldId: number,
    label: string,
    fieldType: string,
    required?: boolean,
    readOnly?: boolean,
    warningOnly?: WarningLevel,
    active?: boolean,
    unit?: string,
    minValue?: number,
    maxValue?: number,
    options?: string[],
    order?: number
  ) => {
    const response = await axios.put(`${API_URL}/qhse/forms/submenus/${submenuId}/fields/update/${fieldId}`, {
      label,
      fieldType,
      required,
      readOnly,
      warningOnly,
      isActive: active,
      unit,
      minValue,
      maxValue,
      optionsJson: options,
      order,
    })
    return response.data
  },
  deleteField: async (submenuId: number, fieldId: number) => {
    const response = await axios.delete(`${API_URL}/qhse/forms/submenus/${submenuId}/fields/delete/${fieldId}`)
    return response.data
  },
  getFieldsForTab: async (tabId: number) => {
    const response = await axios.get(`${API_URL}/qhse/forms/submenus/${tabId}/fields`)
    const fields: QhseFieldResponse[] = response.data ?? []
    return fields.map(mapQhseField)
  },
}
